import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
export const StackedBarChart: React.FC<any> = ({ data }) => {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const colors = [
        "#0141a5",
        "#7d90ff",
        "#52dce3",
        "#4faeeb",
        "#275c99",
        "#08125b",
    ];
    const processData = () => {
        const loanData = data.loan_composition;
        const years = Object.keys(loanData).filter(
            (key) => key !== "focus_quarter"
        );
        const categories = Array.from(
            new Set(
                years.flatMap((year) =>
                    Object.keys(loanData[year] || {}).flatMap((quarter) =>
                        Object.keys(loanData[year][quarter] || {})
                    )
                )
            )
        );
        const categoryLabels = {};
        years.forEach((year) => {
            const yearData = loanData[year];
            Object.keys(yearData || {}).forEach((quarter) => {
                const quarterData = yearData[quarter];
                Object.keys(quarterData || {}).forEach((key) => {
                    categoryLabels[key] = quarterData[key].label;
                });
            });
        });
        const formattedData = years.flatMap((year) =>
            Object.keys(loanData[year] || {}).map((quarter) => {
                const quarterData = loanData[year][quarter];
                const entry = { quarter: `${year} ${quarter}` };
                categories.forEach((category) => {
                    entry[category] = quarterData[category]?.value || 0;
                });
                return entry;
            })
        );
        return { formattedData, categories, categoryLabels };
    };
    useEffect(() => {
        const { formattedData, categories } = processData();
        const margin = { top: 20, right: 80, bottom: 30, left: 70 };
        const width = 590 - margin.left - margin.right; 
        const height = 268 - margin.top - margin.bottom;
        const svg = d3
            .select(svgRef.current)
            .attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom);
        svg.selectAll("*").remove();
        const chart = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);
        const yScale = d3
            .scaleBand()
            .domain(formattedData.map((d) => d.quarter))
            .range([0, height]) 
            .padding(0.2);
        const xScale = d3
            .scaleLinear()
            .domain([
                0,
                d3.max(formattedData, (d: any) =>
                    d3.sum(categories.map((cat) => d[cat]))
                ) || 0,
            ])
            .range([0, width]);
            const colorScale = d3
            .scaleOrdinal()
            .domain(categories)
            .range(colors);
       
        chart.append("g").call(d3.axisLeft(yScale));
        const bottomAxis = chart
            .append("g")
            .attr("transform", `translate(0, ${height})`)
            .call(d3.axisBottom(xScale).ticks(5));
        bottomAxis.select(".domain").remove();
        bottomAxis
            .selectAll(".tick line")
            .attr("y2", -height)
            .attr("stroke", "#ccc");
        const stack = d3.stack().keys(categories);
        // @ts-ignore
        const stackedData = stack(formattedData);
        chart
        .selectAll("g.layer")
        .data(stackedData)
        .enter()
        .append("g")
        .attr("class", "layer")
        .attr("fill", (d: d3.Series<{ [key: string]: number }, string>) =>
          colorScale(d.key || "") as string
        )
        .selectAll("rect")
        .data((d) => d)
        .enter()
        .append("path") 
        .attr("stroke", "transparent")
      .attr("stroke-width", "0") 
      .attr("fill-rule", "evenodd")
        .attr("fill-rule", "evenodd") 
        .attr("d", (d) => {
          const xStart = xScale(d[0]);
          const xEnd = xScale(d[1]);
          const y = yScale(String(d.data.quarter)) || 0;
          const height = yScale.bandwidth();
          const isLastSegment = d[1] === d3.sum(categories.map((cat) => d.data[cat]));
          if (xStart === xEnd) return null;
       
          const roundingRadius = 12;
       
          return isLastSegment
            ? `M${xStart},${y}
               L${xEnd - roundingRadius},${y}
               Q${xEnd},${y} ${xEnd},${y + roundingRadius}
               L${xEnd},${y + height - roundingRadius}
               Q${xEnd},${y + height} ${xEnd - roundingRadius},${y + height}
               L${xStart},${y + height}
               Z`
            : `M${xStart},${y}
               L${xEnd},${y}
               L${xEnd},${y + height}
               L${xStart},${y + height}
               Z`;
        });
        chart
            .selectAll("text.value")
            .data(stackedData.flatMap((layer) => layer))
            .enter()
            .append("text")
            .filter((d) => d[1] - d[0] !== 0)
            .attr("x", (d) => xScale((d[0] + d[1]) / 2))
            .attr("y", (d) => (yScale(String(d.data.quarter)) || 0) + yScale.bandwidth() / 2)
            .attr("dy", "0.35em")
            .attr("text-anchor", "middle")
            .text((d) => d[1] - d[0])
            .style("fill", "#e8dfdf")
            .style("font-size", "8px");
    }, []);
    const loanComposition = data.loan_composition;
  const availableYears = Object.keys(loanComposition);
  if (availableYears.length === 0) {
    return <div>No data available</div>;
  }
  const firstYear = availableYears[0];
const availableQuarters = Object.keys(loanComposition[firstYear] || {});
if (availableQuarters.length === 0) {
  return <div>No data available for year {firstYear}</div>;
}
const firstQuarter = availableQuarters[0];
const selectedQuarter = loanComposition[firstYear]?.[firstQuarter];
 
if (!selectedQuarter) {
  return <div>No data available for {firstYear} {firstQuarter}</div>;
}
 
const labels = Object.values(selectedQuarter).map((loan: any) => loan.label);
    return (
        <div className="stackedBarChart-section">
            <div className="label-stackedBar-section">
                {labels.map((label, index) => (
                    <div key={index} className="stackedBar-cir-lab-sect">
                        <span
                            className="stackedBar-circle"
                            style={{
                                backgroundColor: colors[index % colors.length],
                            }}
                        ></span>
                        <span className="label-stacked-text">{label}</span>
                    </div>
                ))}
            </div>
            <svg ref={svgRef}></svg>
        </div>
    );
};